import { useAuth } from '@/hooks/use-auth';
import { useCustomizationsData } from '@/hooks/useCustomizationsData';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { TenantType } from '@trace-8/trace8-schema';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../basicComponents/button';
import Header from '../header';
import { AssetClassDefaultTargetsPage } from './assetClassDefaultTargets';
import { DealFunnelEditor } from './dealFunnelEditor';
import { ImportData } from './importData';
import { PlatformSettings } from './platformSettings';
import { FirmInfo } from './platformSettings/firmInfo';
import { TemplatesSettings } from './platformSettings/templatesSettings';
import { ISetting } from './platformSettings/templatesSettings/types';
import { SuperAdminSettings } from './superAdminSettings';
import { Users } from './users';

export const getSettingsItemValue = (settings: ISetting[], key: string) => {
  const setting = settings.find((setting) => setting.key === key);
  if (setting?.value) {
    return JSON.parse(setting.value);
  }
  return null;
};

export const Settings = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: customizationsData } = useCustomizationsData();

  const action = searchParams.get('action') || 'default';

  const handleRedirect = (path: string) => {
    navigate(`/settings?action=${path}`);
  };

  const handleClose = () => {
    navigate('/settings');
  };

  if (
    action !== 'default' &&
    user &&
    user.tenant?.type === TenantType.PLATFORM_MANAGER &&
    (user.roles.includes('Super Admin') || user.roles.includes('Admin'))
  ) {
    return (
      <>
        {action === 'importData' && <ImportData backToTitle="Settings" handleCloseImportData={handleClose} />}
        {action === 'dueDiligence' && customizationsData?.menuConfig?.dueDiligence && (
          <DealFunnelEditor backToTitle="Settings" handleClose={handleClose} />
        )}
        {action === 'users' && <Users backToTitle="Settings" handleClose={handleClose} />}
        {action === 'platformSettings' && <PlatformSettings backToTitle="Settings" handleClosePlatformSettings={handleClose} />}
        {action === 'superAdminSettings' && <SuperAdminSettings backToTitle="Settings" handleCloseSuperAdminSettings={handleClose} />}
      </>
    );
  }

  return (
    <>
      {action === 'importData' && <ImportData backToTitle="Settings" handleCloseImportData={handleClose} />}
      {action === 'firmInfo' && <FirmInfo handleClose={handleClose} backToTitle="Settings" />}
      {action === 'templates' && <TemplatesSettings handleClose={handleClose} backToTitle="Settings" />}
      {action === 'assetClasses' && <AssetClassDefaultTargetsPage closeAssetClassTargetsPage={handleClose} backToTitle="Settings" />}
      {action === 'default' && (
        <MainWrap>
          <Header />
          <SettingsContainer>
            {user && user.tenant?.type === TenantType.PLATFORM_MANAGER && (user.roles.includes('Super Admin') || user.roles.includes('Admin')) && (
              <SettingsWrapper>
                <SettingsItem>
                  <SettingsItemTitle id="itemTitle" onClick={() => handleRedirect('importData')}>
                    Import Data
                  </SettingsItemTitle>
                  <SettingsItemButtonWrapper id="openButton">
                    <Button onClick={() => handleRedirect('importData')}>Open</Button>
                  </SettingsItemButtonWrapper>
                </SettingsItem>
                {customizationsData?.menuConfig?.dueDiligence && (
                  <SettingsItem>
                    <SettingsItemTitle id="itemTitle" onClick={() => handleRedirect('dueDiligence')}>
                      Due Diligence
                    </SettingsItemTitle>
                    <SettingsItemButtonWrapper id="openButton">
                      <Button onClick={() => handleRedirect('dueDiligence')}>Open</Button>
                    </SettingsItemButtonWrapper>
                  </SettingsItem>
                )}
                <SettingsItem>
                  <SettingsItemTitle id="itemTitle" onClick={() => handleRedirect('users')}>
                    Users
                  </SettingsItemTitle>
                  <SettingsItemButtonWrapper id="openButton">
                    <Button onClick={() => handleRedirect('users')}>Open</Button>
                  </SettingsItemButtonWrapper>
                </SettingsItem>
                <SettingsItem>
                  <SettingsItemTitle id="itemTitle" onClick={() => handleRedirect('platformSettings')}>
                    Platform Settings
                  </SettingsItemTitle>
                  <SettingsItemButtonWrapper id="openButton">
                    <Button onClick={() => handleRedirect('platformSettings')}>Open</Button>
                  </SettingsItemButtonWrapper>
                </SettingsItem>
                <SettingsItem>
                  <SettingsItemTitle id="itemTitle" onClick={() => handleRedirect('superAdminSettings')}>
                    Super Admin
                  </SettingsItemTitle>
                  <SettingsItemButtonWrapper id="openButton">
                    <Button onClick={() => handleRedirect('superAdminSettings')}>Open</Button>
                  </SettingsItemButtonWrapper>
                </SettingsItem>
              </SettingsWrapper>
            )}

            {user && user.tenant?.type === TenantType.ADVISORY_FIRM && (
              <SettingsWrapper>
                <SettingsItem>
                  <SettingsItemTitle id="itemTitle" onClick={() => handleRedirect('importData')}>
                    Import Data
                  </SettingsItemTitle>
                  <SettingsItemButtonWrapper id="openButton">
                    <Button onClick={() => handleRedirect('importData')}>Open</Button>
                  </SettingsItemButtonWrapper>
                </SettingsItem>
                <SettingsItem>
                  <SettingsItemTitle id="itemTitle" onClick={() => handleRedirect('assetClasses')}>
                    Asset Classes
                  </SettingsItemTitle>
                  <SettingsItemButtonWrapper id="openButton">
                    <Button onClick={() => handleRedirect('assetClasses')}>Open</Button>
                  </SettingsItemButtonWrapper>
                </SettingsItem>
                <SettingsItem>
                  <SettingsItemTitle id="itemTitle" onClick={() => handleRedirect('firmInfo')}>
                    Firm Info
                  </SettingsItemTitle>
                  <SettingsItemButtonWrapper id="openButton">
                    <Button onClick={() => handleRedirect('firmInfo')}>Open</Button>
                  </SettingsItemButtonWrapper>
                </SettingsItem>
                <SettingsItem>
                  <SettingsItemTitle id="itemTitle" onClick={() => handleRedirect('templates')}>
                    Templates
                  </SettingsItemTitle>
                  <SettingsItemButtonWrapper id="openButton">
                    <Button onClick={() => handleRedirect('templates')}>Open</Button>
                  </SettingsItemButtonWrapper>
                </SettingsItem>
              </SettingsWrapper>
            )}
          </SettingsContainer>
        </MainWrap>
      )}
    </>
  );
};

export const SettingsWrapper = styled.div`
  background: ${themeVar('layer.1')};
  padding: 40px 60px;
  border: 1px solid ${themeVar('border.base')};
  border-radius: 10px;
`;

export const SettingsItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 21px 14px;
  border-bottom: 1px solid ${themeVar('border.base')};

  &:hover #openButton {
    opacity: 1;
    transition: background-color 100ms ease;
  }
  &:hover #itemTitle {
    color: ${themeVar('font.action')};
  }
  &:hover {
    background-color: ${themeVar('layer.2')};
    transition: background-color 100ms ease;
  }
`;

export const SettingsItemTitle = styled.div`
  font-weight: 600;
  font-size: 16px;
  line-height: 22px;
  color: ${themeVar('font.strong')};
  cursor: pointer;
`;

export const SettingsItemButtonWrapper = styled.div`
  opacity: 0;
`;

export const SettingsContainer = styled(PaddingWrap)`
  padding-top: 50px;
  padding-bottom: 50px;
`;

export const SettingsSection = styled.div<{ isMobile: boolean }>`
  border-radius: 10px;
  padding: ${({ isMobile }) => (isMobile ? '30px' : '40px')};
  border: 1px solid ${themeVar('border.base')};
  background-color: ${themeVar('layer.1')};
`;
